import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/base/PageLayout';
import HubCards from '../../components/feature/HubCards';

const Wisdom: React.FC = () => {
  useEffect(() => {
    document.title = '執事の知恵 - プロ執事が教えるマナー・おもてなし・品格の専門知識｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが培った専門知識を体系的にまとめた「執事の知恵」。おもてなしの心、フォーマルマナー、執事スキル、日常の品格など、カテゴリー別に実践的なノウハウを解説。テーブルマナーからVIP対応、時間管理術まで、ビジネスシーンや日常生活で活用できる本格的な執事の技術と心構えを学べます。');
    }

    // Add Keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '執事の知恵, マナー, おもてなし, 品格, ホスピタリティ');
    } else {
      const keywordsTag = document.createElement('meta');
      keywordsTag.name = 'keywords';
      keywordsTag.content = '執事の知恵, マナー, おもてなし, 品格, ホスピタリティ';
      document.head.appendChild(keywordsTag);
    }

    // Add Canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/wisdom`);
    } else {
      const canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/wisdom`;
      document.head.appendChild(canonical);
    }

    // Add Last-modified
    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    } else {
      const lastModTag = document.createElement('meta');
      lastModTag.name = 'last-modified';
      lastModTag.content = new Date().toISOString().split('T')[0];
      document.head.appendChild(lastModTag);
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "執事の知恵",
      "description": "執事ベルが20年以上の経験から培った専門知識を体系的にまとめた「執事の知恵」。おもてなしの心、フォーマルマナー、執事スキル、日常の品格など、カテゴリー別に実践的なノウハウを解説。",
      "url": `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/wisdom`,
      "about": {
        "@type": "Thing",
        "name": "執事文化とマナー"
      },
      "hasPart": [
        {
          "@type": "WebPage",
          "name": "おもてなしの心",
          "url": `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/wisdom/hospitality`
        },
        {
          "@type": "WebPage",
          "name": "フォーマルマナー",
          "url": `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/wisdom/etiquette`
        },
        {
          "@type": "WebPage",
          "name": "執事スキル",
          "url": `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/wisdom/butler-skills`
        },
        {
          "@type": "WebPage",
          "name": "日常の品格",
          "url": `${import.meta.env.VITE_SITE_URL || "https://butlerbell.jp"}/wisdom/elegance`
        }
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const categories = [
    {
      title: 'おもてなしの心',
      description: '真のホスピタリティとサービスの本質を学ぶ',
      path: '/wisdom/hospitality',
      image: '/images/generated/seq-wisdom-hospitality-hands-ceba44.jpg'
    },
    {
      title: 'フォーマルマナー',
      description: '正式な場での立ち振る舞いとエチケット',
      path: '/wisdom/etiquette',
      image: '/images/generated/seq-wisdom-etiquette-table-b5d105.jpg'
    },
    {
      title: '執事スキル',
      description: 'プロフェッショナルな執事の技術と知識',
      path: '/wisdom/butler-skills',
      image: '/images/generated/seq-wisdom-butler-hands-5d2a6e.jpg'
    },
    {
      title: '日常の品格',
      description: '毎日の生活に活かせる上品な所作',
      path: '/wisdom/elegance',
      image: '/images/generated/seq-wisdom-elegance-lifestyle-b522b1.jpg'
    }
  ];

  const featuredArticles = [
    {
      title: 'VIP対応の極意',
      excerpt: '特別なお客様への最高級のサービスを提供するための心構えと技術',
      path: '/wisdom/articles/vip-service',
      category: 'おもてなしの心'
    },
    {
      title: 'テーブルマナーの美学',
      excerpt: '正式な食事の場での美しい立ち振る舞いと作法の基本',
      path: '/wisdom/articles/table-manners',
      category: 'フォーマルマナー'
    },
    {
      title: '美しい所作',
      excerpt: '日常の動作を上品で洗練されたものにするための技術',
      path: '/wisdom/articles/posture',
      category: '日常の品格'
    }
  ];

  return (
    <PageLayout hero={<>
<section 
          className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('/images/gakken/gk-09.jpg')`
          }}
        >
          <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <div className="mb-4 md:mb-8">
              <span className="inline-block bg-amber-600/20 backdrop-blur-sm text-amber-200 px-3 md:px-6 py-2 md:py-3 rounded-full text-xs md:text-sm font-medium border border-amber-400/30">
                Butler's Wisdom
              </span>
            </div>
            <h1 className="text-2xl md:text-7xl font-serif text-white mb-4 md:mb-8 leading-tight">
              執事の知恵
            </h1>
            <p className="text-sm md:text-2xl text-amber-100 max-w-6xl mx-auto leading-relaxed mb-6 md:mb-12 px-5 sm:px-6 lg:px-8">
              執事文化の研究と実践を通じて学んだ、真のおもてなしと品格ある生き方の智慧をお伝えします
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-5 sm:px-6 lg:px-8">
              <Link
                to="#categories"
                className="inline-block bg-amber-700 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-amber-800 transition-all duration-300 transform hover:scale-105 whitespace-nowrap text-sm md:text-base"
              >
                知恵を探る
              </Link>
              <Link
                to="/wisdom/articles/vip-service"
                className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap text-sm md:text-base"
              >
                注目記事を読む
              </Link>
            </div>
          </div>
          
          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        

        {/* Categories Section */}
        <section id="categories" className="py-12 md:py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-20">
              <div className="mb-3 md:mb-6">
                <span className="inline-block bg-amber-100 text-amber-800 px-3 md:px-5 sm:px-6 lg:px-8 py-1 md:py-2 rounded-full text-xs md:text-sm font-medium">
                  Categories
                </span>
              </div>
              <h2 className="text-2xl md:text-5xl font-serif text-gray-900 mb-3 md:mb-6">
                知恵のカテゴリー
              </h2>
              <p className="text-sm md:text-xl text-gray-600 max-w-6xl mx-auto leading-relaxed px-5 sm:px-6 lg:px-8">
                それぞれの分野で培った専門知識と実践的なノウハウをご紹介
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
              {categories.map((category, index) => (
                <Link
                  key={index}
                  to={category.path}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.title}
                      className="w-full h-48 md:h-72 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6 md:p-8 lg:p-10">
                    <h3 className="text-lg md:text-2xl lg:text-3xl font-serif text-gray-900 mb-2 md:mb-4 group-hover:text-amber-700 transition-colors duration-300">
                      {category.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-6 lg:text-lg">
                      {category.description}
                    </p>
                    <div className="flex items-center text-amber-700 group-hover:text-amber-800 font-medium text-sm md:text-base">
                      <span>詳しく見る</span>
                      <i className="ri-arrow-right-line ml-2 w-5 h-5 flex items-center justify-center transform group-hover:translate-x-1 transition-transform duration-300"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-12 md:py-24 bg-gradient-to-b from-amber-50 to-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-20">
              <div className="mb-3 md:mb-6">
                <span className="inline-block bg-amber-200 text-amber-800 px-3 md:px-5 sm:px-6 lg:px-8 py-1 md:py-2 rounded-full text-xs md:text-sm font-medium">
                  Featured Articles
                </span>
              </div>
              <h2 className="text-2xl md:text-5xl font-serif text-gray-900 mb-3 md:mb-6">
                注目の記事
              </h2>
              <p className="text-sm md:text-xl text-gray-600 max-w-6xl mx-auto leading-relaxed px-5 sm:px-6 lg:px-8">
                特に人気の高い実践的な知恵をピックアップ
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {featuredArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.path}
                  className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="p-6 md:p-8">
                    <div className="text-xs md:text-sm text-amber-700 font-medium mb-2 md:mb-3 bg-amber-50 px-3 py-1 rounded-full inline-block">
                      {article.category}
                    </div>
                    <h3 className="text-base md:text-xl lg:text-2xl font-serif text-gray-900 mb-2 md:mb-4 group-hover:text-amber-700 transition-colors duration-300">
                      {article.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-6">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center text-amber-700 group-hover:text-amber-800 font-medium text-sm md:text-base">
                      <span>記事を読む</span>
                      <i className="ri-arrow-right-line ml-2 w-4 h-4 flex items-center justify-center transform group-hover:translate-x-1 transition-transform duration-300"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-24 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-16">
              <h2 className="text-xl md:text-3xl lg:text-4xl font-serif mb-3 md:mb-6">
                執事の知恵を共有する理由
              </h2>
              <p className="text-sm md:text-xl text-gray-300 max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
                執事文化の本質を広く共有し、人々の暮らしをより豊かにすることを目指しています
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {[
                { icon: 'ri-heart-line', label: '相手を思いやる心' },
                { icon: 'ri-book-open-line', label: '継続的な学び' },
                { icon: 'ri-lightbulb-line', label: '実践的な知恵' },
                { icon: 'ri-team-line', label: '共に成長する姿勢' }
              ].map((value, index) => (
                <div key={index} className="text-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-2 md:mb-4 bg-amber-600 rounded-full">
                    <i className={`${value.icon} text-lg md:text-2xl text-white`}></i>
                  </div>
                  <div className="text-gray-300 text-xs md:text-lg">
                    {value.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-24 bg-gradient-to-r from-amber-600 to-amber-700">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl md:text-5xl font-serif text-white mb-4 md:mb-8">
              より深い学びをお求めの方へ
            </h2>
            <p className="text-sm md:text-xl lg:text-2xl text-amber-100 mb-6 md:mb-12 leading-relaxed px-5 sm:px-6 lg:px-8">
              個別指導やセミナーを通じて、実践的なスキルを身につけませんか
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-5 sm:px-6 lg:px-8">
              <Link
                to="/services"
                className="inline-block bg-white text-amber-800 px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-amber-50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap text-sm md:text-base"
              >
                サービス一覧を見る
              </Link>
              <Link
                to="/contact"
                className="inline-block bg-amber-800 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-amber-900 transition-all duration-300 border border-amber-500 whitespace-nowrap text-sm md:text-base"
              >
                お問い合わせ
              </Link>
            </div>
          </div>
        </section>
      </div>
          <HubCards group="wisdom" />
    </PageLayout>
  );
};

export default Wisdom;
